import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, toFormErrors } from '@/lib/validationErrors';
import { roleListService } from '@/services/identity/roleListService';
import type { ListViewExtraProps } from '@/config/listViewExtras';

/** The server's first gate on duplicate: creating the role. */
export const CREATE_ROLE_PERMISSION = 'AbpIdentity.Roles.Create';

/**
 * The server's second gate: writing the new role's permission grants — the same permission
 * ABP's permission UI requires before it changes a role's grants.
 */
export const MANAGE_ROLE_PERMISSIONS_PERMISSION = 'AbpIdentity.Roles.ManagePermissions';

const DUPLICATE_ROLE_PERMISSIONS = [CREATE_ROLE_PERMISSION, MANAGE_ROLE_PERMISSIONS_PERMISSION];

interface DuplicateRoleDialogProps {
  open: boolean;
  sourceRoleId: string;
  sourceRoleName: string;
  onClose: () => void;
  /** Called with the new role's id; the caller opens it for the adjustments the copy was made for. */
  onDuplicated: (newRoleId: string) => void;
}

function DuplicateRoleDialog({ open, sourceRoleId, sourceRoleName, onClose, onDuplicated }: DuplicateRoleDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  // The suggested name follows the source; typing anything replaces it wholesale.
  const effectiveName = name !== '' ? name : `${sourceRoleName}-copy`;

  const reset = () => {
    setName('');
    setApiValidationErrors([]);
  };

  const mutation = useMutation({
    mutationFn: () => roleListService.duplicate(sourceRoleId, effectiveName.trim()),
    onSuccess: (created) => {
      reset();
      onDuplicated(created.id);
    },
    onError: (error: unknown) => setApiValidationErrors(
      toFormErrors(error, t('common.genericError', { defaultValue: 'An error occurred' })),
    ),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('identity.duplicateRole')}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('identity.duplicateRoleIntro', { name: sourceRoleName })}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label={t('identity.newRoleName')}
          value={effectiveName}
          onChange={(e) => setName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={effectiveName.trim() === '' || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {t('identity.duplicateRole')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Toolbar action on the Roles grid: create a new role with the same flags and permission grants
 * as the selected one (server-side copy; the copy is never static). Acts on exactly one role, and
 * opens the new role afterwards — a duplicate exists to be adjusted.
 */
export function DuplicateRoleButton({ selectedIds, selectedRows }: ListViewExtraProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allowed = useHasPermission(DUPLICATE_ROLE_PERMISSIONS);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!allowed) return null;

  const sourceRoleId = selectedIds.length === 1 ? selectedIds[0] : null;
  // The row may not be loaded (resolveSelectedRows); without it there is no name to suggest,
  // so the dialog simply opens with an empty suggestion base.
  const sourceRoleName = String(
    selectedRows.find((row) => row.id === sourceRoleId)?.name ?? '',
  );

  return (
    <>
      <Button
        startIcon={<ContentCopy />}
        disabled={sourceRoleId === null}
        onClick={() => setDialogOpen(true)}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('identity.duplicateRole')}
      </Button>

      {sourceRoleId !== null && (
        <DuplicateRoleDialog
          open={dialogOpen}
          sourceRoleId={sourceRoleId}
          sourceRoleName={sourceRoleName}
          onClose={() => setDialogOpen(false)}
          onDuplicated={(newRoleId) => {
            setDialogOpen(false);
            navigate(`/core/roles/${newRoleId}`);
          }}
        />
      )}
    </>
  );
}
